import { useState, useEffect, useMemo, useCallback } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { format, addDays, startOfDay, parseISO } from "date-fns";
import { useProfileStore } from "@/store/profile";
import { bookingClient } from "@/api/booking-client";
import type { AvailableTime } from "@/types/booking";
import { cn } from "@/utils/cn";

interface ScheduleModalProps {
  open: boolean;
  onClose: () => void;
  locationCode: string;
  onBooked?: () => void;
}

type Step = "date" | "time" | "confirm" | "success";

export function ScheduleModal({
  open,
  onClose,
  locationCode,
  onBooked,
}: ScheduleModalProps) {
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const authInfo = useProfileStore((s) => s.profileAuthInfo);

  const [step, setStep] = useState<Step>("date");
  const [availableTimes, setAvailableTimes] = useState<AvailableTime[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState<AvailableTime | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAvailableTimes = useCallback(async () => {
    if (!currentPatient) return;
    setLoading(true);
    setError(null);
    try {
      const tomorrow = addDays(new Date(), 1);
      const res = await bookingClient.getExistingPatientApptTimes({
        locationCode,
        startDate: format(tomorrow, "yyyy-MM-dd"),
        numDays: 14,
        daysOfWeek: null,
        firstName: currentPatient.firstName,
        lastName: currentPatient.lastName,
        mobilePhone: null,
        email: authInfo?.email ?? null,
      });
      setAvailableTimes(res.data.availableTimes ?? []);
    } catch {
      setError("Unable to load available times. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [currentPatient, authInfo, locationCode]);

  useEffect(() => {
    if (!open) return;
    setStep("date");
    setSelectedDate(undefined);
    setSelectedTime(null);
    setError(null);
    setAvailableTimes([]);
    fetchAvailableTimes();
  }, [open, fetchAvailableTimes]);

  // ---- Derived data -------------------------------------------------------

  const timesByDate = useMemo(() => {
    const map = new Map<string, AvailableTime[]>();
    for (const t of availableTimes) {
      const key = t.apptDateTime.split("T")[0]; // "2026-01-15"
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    return map;
  }, [availableTimes]);

  const availableDateSet = useMemo(
    () => new Set(timesByDate.keys()),
    [timesByDate],
  );

  const availableDatesForCalendar = useMemo(
    () => [...availableDateSet].map((d) => startOfDay(parseISO(d))),
    [availableDateSet],
  );

  const timesForDate = useMemo(() => {
    if (!selectedDate) return [];
    const key = format(selectedDate, "yyyy-MM-dd");
    return timesByDate.get(key) ?? [];
  }, [selectedDate, timesByDate]);

  // ---- Handlers -----------------------------------------------------------

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    const key = format(date, "yyyy-MM-dd");
    if (!availableDateSet.has(key)) return;
    setSelectedDate(date);
    setSelectedTime(null);
    setStep("time");
  };

  const handleTimeSelect = (time: AvailableTime) => {
    setSelectedTime(time);
    setStep("confirm");
  };

  const handleBook = async () => {
    if (!selectedTime || !currentPatient) return;
    setSubmitting(true);
    setError(null);
    try {
      await bookingClient.insertExistingPatientWithAppt({
        locationCode,
        patientID: currentPatient.patientID,
        firstName: currentPatient.firstName,
        lastName: currentPatient.lastName,
        mobilePhone: null,
        email: authInfo?.email ?? null,
        apptDateTime: selectedTime.apptDateTime,
        visitCode: selectedTime.visitCode,
        providerCode: selectedTime.providerCode,
        webSource: "Patient Portal",
        ipAddress: null,
        notes: null,
      });
      setStep("success");
      onBooked?.();
    } catch {
      setError("Failed to book appointment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const displayTime = (t: AvailableTime) =>
    t.apptTime ?? format(parseISO(t.apptDateTime), "h:mm a");

  // ---- Render -------------------------------------------------------------

  if (!open) return null;

  const tomorrow = addDays(startOfDay(new Date()), 1);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90dvh] w-full max-w-xl overflow-y-auto rounded-lg bg-white p-4 shadow-xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="Close"
        >
          <i className="fas fa-times" />
        </button>

        {/* Title */}
        <h2 className="mb-4 pr-12 text-lg font-semibold text-gray-800">
          {step === "success" ? "Appointment Booked" : "Schedule Appointment"}
        </h2>

        {/* Error banner */}
        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="ml-2 text-sm text-muted-foreground">
              Loading available times…
            </span>
          </div>
        )}

        {/* ---- Step: Date ---- */}
        {!loading && step === "date" && (
          <>
            <p className="mb-3 text-sm text-muted-foreground">
              Select a date with available times:
            </p>

            {availableTimes.length === 0 && !error && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No available appointment times in the next 14 days.
              </p>
            )}

            {availableTimes.length > 0 && (
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={(date: Date) => {
                  if (date < tomorrow) return true;
                  return !availableDateSet.has(format(date, "yyyy-MM-dd"));
                }}
                modifiers={{ available: availableDatesForCalendar }}
                modifiersClassNames={{
                  available: "!bg-blue-100 !font-semibold !text-blue-900",
                }}
                className="mx-auto"
              />
            )}
          </>
        )}

        {/* ---- Step: Time ---- */}
        {step === "time" && selectedDate && (
          <>
            <button
              onClick={() => setStep("date")}
              className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm text-primary hover:underline"
            >
              <i className="fas fa-arrow-left text-xs" />
              Back to calendar
            </button>

            <p className="mb-3 text-sm text-muted-foreground">
              Available times for{" "}
              <span className="font-medium text-gray-800">
                {format(selectedDate, "EEEE, MMMM d, yyyy")}
              </span>
            </p>

            {timesForDate.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No times available for this date.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {timesForDate.map((t, i) => (
                  <button
                    key={i}
                    onClick={() => handleTimeSelect(t)}
                    className={cn(
                      "min-h-11 rounded-md border border-gray-200 px-3 py-2.5 text-sm font-medium transition-colors",
                      "hover:border-primary hover:bg-primary/5 hover:text-primary",
                      "focus:outline-none focus:ring-2 focus:ring-primary/30",
                    )}
                  >
                    {displayTime(t)}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* ---- Step: Confirm ---- */}
        {step === "confirm" && selectedTime && selectedDate && (
          <>
            <button
              onClick={() => setStep("time")}
              className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm text-primary hover:underline"
            >
              <i className="fas fa-arrow-left text-xs" />
              Change time
            </button>

            <div className="mb-6 space-y-3 rounded-lg border border-gray-100 bg-gray-50 p-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Date
                </p>
                <p className="text-sm font-semibold text-gray-800">
                  {format(selectedDate, "EEEE, MMMM d, yyyy")}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Time
                </p>
                <p className="text-sm font-semibold text-gray-800">
                  {displayTime(selectedTime)}
                </p>
              </div>
            </div>

            <button
              onClick={handleBook}
              disabled={submitting}
              className={cn(
                "inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-3 text-sm font-medium text-primary-foreground",
                "hover:opacity-90 disabled:opacity-50",
              )}
            >
              {submitting ? (
                <>
                  <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                  Booking…
                </>
              ) : (
                "Confirm Appointment"
              )}
            </button>
          </>
        )}

        {/* ---- Step: Success ---- */}
        {step === "success" && selectedTime && selectedDate && (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <i className="fas fa-check text-2xl text-green-600" />
            </div>
            <p className="mb-1 text-sm text-muted-foreground">
              Your appointment is confirmed for
            </p>
            <p className="text-lg font-semibold text-gray-800">
              {format(selectedDate, "EEEE, MMMM d")}
            </p>
            <p className="text-lg font-semibold text-gray-800">
              {displayTime(selectedTime)}
            </p>
            <button
              onClick={onClose}
              className="mt-6 min-h-11 rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
